<template>
  <div class="task-list">
    <div class="task-list__toolbar">
      <div class="task-list__summary">
        <span class="task-list__summary-count">
          {{ completedCount }}/{{ tasks.length }}
        </span>
        <span class="text-muted">tarefas concluídas</span>
        <div class="task-list__summary-bar">
          <div
            class="task-list__summary-fill"
            :style="{ width: `${overallPercent}%` }"
          />
        </div>
      </div>

      <div class="task-list__toolbar-actions">
        <b-form-checkbox v-model="hideCompleted" switch class="mr-1">
          <small class="text-muted">Ocultar concluídas</small>
        </b-form-checkbox>
        <b-button variant="primary" @click="openCreateModal">
          <feather-icon icon="PlusIcon" class="mr-50" /> Nova tarefa
        </b-button>
      </div>
    </div>

    <div v-if="!tasks.length" class="task-empty">
      <feather-icon icon="CheckSquareIcon" size="34" class="text-muted mb-1" />
      <h6 class="mb-25">Nenhuma tarefa cadastrada ainda</h6>
      <small class="text-muted">
        Crie a primeira tarefa para começar a acompanhar o plano do cliente.
      </small>
    </div>

    <div v-for="group in visibleGroups" :key="group.key" class="task-group">
      <div class="task-group__header">
        <span
          class="group-color-dot"
          :style="{ backgroundColor: group.color || '#b9b9c3' }"
        />
        <h6 class="task-group__title mb-0">{{ group.title }}</h6>
        <b-badge pill variant="light-secondary" class="ml-50">
          {{ groupProgress(group) }}
        </b-badge>
      </div>

      <draggable
        v-model="group.tasks"
        group="plan-implementation-tasks"
        handle=".draggable-task-handle"
        tag="ul"
        class="task-list-group"
        ghost-class="task-item--ghost"
        :data-group-key="group.key"
        @change="(evt) => onChange(group, evt)"
      >
        <li
          v-for="task in group.tasks"
          v-show="!hideCompleted || !task.completed_at"
          :key="task.id"
          class="task-item"
          :class="{
            'task-item--done': !!task.completed_at,
            'task-item--overdue': isOverdue(task),
          }"
          :style="{ '--task-accent': group.color || '#7367f0' }"
        >
          <feather-icon
            icon="MoreVerticalIcon"
            class="draggable-task-handle"
          />

          <b-form-checkbox
            :checked="!!task.completed_at"
            class="task-item__check"
            @change="(checked) => toggleComplete(task, checked)"
          />

          <div class="task-content">
            <div class="d-flex justify-content-between align-items-start">
              <div class="task-main">
                <span class="task-title">{{ task.title }}</span>
                <div v-if="task.description" class="task-description">
                  {{ task.description }}
                </div>
                <div class="task-meta">
                  <b-badge
                    v-if="task.due_date"
                    pill
                    :variant="dueVariant(task)"
                    class="task-meta__badge"
                  >
                    <feather-icon icon="CalendarIcon" size="11" class="mr-25" />
                    {{ dueLabel(task) }}
                  </b-badge>
                  <b-badge
                    v-if="task.completed_at"
                    pill
                    variant="light-success"
                    class="task-meta__badge"
                  >
                    <feather-icon icon="CheckIcon" size="11" class="mr-25" />
                    {{ completionLabel(task) }}
                  </b-badge>
                </div>
              </div>

              <b-button
                variant="flat-danger"
                size="sm"
                class="btn-icon task-item__delete"
                @click="removeTask(task)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </div>
        </li>

        <li
          v-if="!group.tasks.length"
          :key="`${group.key}-empty`"
          class="task-placeholder"
        >
          Arraste tarefas para cá
        </li>
      </draggable>
    </div>

    <!-- Create Task Modal -->
    <b-modal
      id="task-create-modal"
      title="Nova tarefa"
      ok-title="Salvar"
      cancel-title="Cancelar"
      :ok-disabled="!form.title || saving"
      @ok.prevent="submitTask"
      centered
    >
      <b-form-group label="Título">
        <b-form-input v-model="form.title" placeholder="Ex.: Abrir conta na corretora" />
      </b-form-group>

      <b-form-group label="Descrição">
        <b-form-textarea v-model="form.description" rows="3" />
      </b-form-group>

      <b-form-group label="Prazo">
        <b-form-input v-model="form.due_date" type="date" />
      </b-form-group>

      <b-form-group label="Marco (opcional)">
        <b-form-select v-model="form.milestone_id" :options="milestoneOptions" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BBadge,
  BModal,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormCheckbox,
} from "bootstrap-vue";
import draggable from "vuedraggable";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import moment from "moment";
import * as types from "../store/types";

const UNASSIGNED_KEY = "unassigned";

export default {
  components: {
    BButton,
    BBadge,
    BModal,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormCheckbox,
    draggable,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      saving: false,
      hideCompleted: false,
      groups: [],
      form: this.blankForm(),
    };
  },
  computed: {
    ...mapGetters({
      tasks: types.TASKS,
      milestones: types.MILESTONES,
    }),
    completedCount() {
      return this.tasks.filter((task) => !!task.completed_at).length;
    },
    overallPercent() {
      if (!this.tasks.length) return 0;
      return Math.round((this.completedCount / this.tasks.length) * 100);
    },
    visibleGroups() {
      return this.groups.filter(
        (group) => group.tasks.length || group.key === UNASSIGNED_KEY
      );
    },
    milestoneOptions() {
      return [
        { value: null, text: "Nenhum (tarefa avulsa)" },
        ...this.milestones.map((milestone) => ({
          value: milestone.id,
          text: milestone.title,
        })),
      ];
    },
  },
  watch: {
    tasks() {
      this.rebuildGroups();
    },
    milestones() {
      this.rebuildGroups();
    },
  },
  mounted() {
    this.fetchAll();
  },
  methods: {
    ...mapActions({
      getTasks: types.GET_TASKS,
      getMilestones: types.GET_MILESTONES,
      createTask: types.CREATE_TASK,
      updateTask: types.UPDATE_TASK,
      deleteTask: types.DELETE_TASK,
    }),
    blankForm() {
      return {
        title: "",
        description: "",
        due_date: null,
        milestone_id: null,
      };
    },
    fetchAll() {
      this.getTasks(this.token).catch(() => this.notifyError());
      this.getMilestones(this.token).catch(() => this.notifyError());
    },
    rebuildGroups() {
      const byMilestone = {};
      const unassigned = [];

      this.tasks.forEach((task) => {
        if (task.milestone_id) {
          if (!byMilestone[task.milestone_id]) {
            byMilestone[task.milestone_id] = [];
          }
          byMilestone[task.milestone_id].push(task);
        } else {
          unassigned.push(task);
        }
      });

      const sortByOrder = (list) =>
        [...list].sort((a, b) => (a.order || 0) - (b.order || 0));

      const groups = this.milestones.map((milestone) => ({
        key: String(milestone.id),
        id: milestone.id,
        title: milestone.title,
        color: milestone.color,
        tasks: sortByOrder(byMilestone[milestone.id] || []),
      }));

      groups.push({
        key: UNASSIGNED_KEY,
        id: null,
        title: "Avulsas",
        color: null,
        tasks: sortByOrder(unassigned),
      });

      this.groups = groups;
    },
    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    groupProgress(group) {
      const done = group.tasks.filter((task) => !!task.completed_at).length;
      return `${done}/${group.tasks.length}`;
    },
    isOverdue(task) {
      if (task.completed_at || !task.due_date) return false;
      return moment(task.due_date).startOf("day").isBefore(moment().startOf("day"));
    },
    dueVariant(task) {
      if (task.completed_at) return "light-secondary";
      if (this.isOverdue(task)) return "light-danger";
      const days = moment(task.due_date)
        .startOf("day")
        .diff(moment().startOf("day"), "days");
      return days <= 3 ? "light-warning" : "light-secondary";
    },
    dueLabel(task) {
      const formatted = this.formatDate(task.due_date);
      if (task.completed_at) return formatted;
      if (this.isOverdue(task)) return `Atrasada · ${formatted}`;
      return `Prazo: ${formatted}`;
    },
    completionLabel(task) {
      if (!task.completed_at) return "";
      if (task.completion_source === "CLIENT") return "Concluída pelo cliente";
      if (task.completion_source === "PLANNER") return "Concluída pelo planejador";
      return "Concluída";
    },
    openCreateModal() {
      this.form = this.blankForm();
      this.$bvModal.show("task-create-modal");
    },
    submitTask(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      if (!this.form.title) return;
      this.saving = true;

      this.createTask({
        token: this.token,
        title: this.form.title,
        description: this.form.description || null,
        due_date: this.form.due_date || null,
        milestone_id: this.form.milestone_id || null,
      })
        .then(() => {
          this.$bvModal.hide("task-create-modal");
          this.fetchAll();
          this.notify("Tarefa criada!", "A tarefa foi criada com sucesso.", "success");
        })
        .catch(() => {
          this.notify(
            "Oops!",
            "Não foi possível criar a tarefa. Tente novamente.",
            "danger"
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    toggleComplete(task, checked) {
      const payload = checked
        ? {
            token: this.token,
            id: task.id,
            completed_at: new Date().toISOString(),
            completion_updated_at: new Date().toISOString(),
            completion_source: "PLANNER",
          }
        : {
            token: this.token,
            id: task.id,
            completed_at: null,
            completion_updated_at: new Date().toISOString(),
            completion_source: null,
          };

      this.updateTask(payload)
        .then(() => {
          this.fetchAll();
        })
        .catch(() => {
          this.notifyError();
        });
    },
    onChange(group, evt) {
      if (evt.added) {
        const task = evt.added.element;
        this.updateTask({
          token: this.token,
          id: task.id,
          milestone_id: group.id,
          order: evt.added.newIndex,
        })
          .then(() => this.fetchAll())
          .catch(() => this.notifyError());
        return;
      }
      if (evt.moved) {
        const task = evt.moved.element;
        this.updateTask({
          token: this.token,
          id: task.id,
          order: evt.moved.newIndex,
        })
          .then(() => this.fetchAll())
          .catch(() => this.notifyError());
      }
    },
    async removeTask(task) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Deseja realmente excluir a tarefa "${task.title}"?`,
        {
          title: "Excluir tarefa",
          okTitle: "Excluir",
          cancelTitle: "Cancelar",
          okVariant: "danger",
          centered: true,
        }
      );
      if (!confirmed) return;

      this.deleteTask({ token: this.token, id: task.id })
        .then(() => {
          this.fetchAll();
          this.notify("Tarefa excluída!", "A tarefa foi excluída com sucesso.", "success");
        })
        .catch(() => {
          this.notifyError();
        });
    },
    notify(title, text, variant) {
      this.toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          icon: variant === "danger" ? "AlertTriangleIcon" : "CheckCircleIcon",
          variant,
        },
      });
    },
    notifyError() {
      this.notify(
        "Oops!",
        "Ocorreu um erro. Entre em contato com o setor de TI.",
        "danger"
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.task-list__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.task-list__summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.9rem;
}
.task-list__summary-count {
  font-weight: 600;
  font-size: 1.1rem;
}
.task-list__summary-bar {
  width: 140px;
  height: 6px;
  border-radius: 99px;
  background-color: rgba(115, 103, 240, 0.15);
  overflow: hidden;
}
.task-list__summary-fill {
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #7367f0, #00cfe8);
  transition: width 0.5s ease;
}
.task-list__toolbar-actions {
  display: flex;
  align-items: center;
}
.task-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.5rem 1rem;
  border: 1px dashed rgba(34, 41, 47, 0.15);
  border-radius: 10px;
}
.task-group {
  margin-bottom: 1.75rem;
}
.task-group__header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}
.task-group__title {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.78rem;
  color: #6e6b7b;
}
.group-color-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}
.task-list-group {
  list-style: none;
  padding: 0;
  margin: 0;
  min-height: 20px;
}
.task-item {
  --task-accent: #7367f0;
  display: flex;
  align-items: flex-start;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(34, 41, 47, 0.08);
  border-left: 3px solid var(--task-accent);
  border-radius: 8px;
  margin-bottom: 0.6rem;
  background-color: #fff;
  position: relative;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(34, 41, 47, 0.1);

    .task-item__delete {
      opacity: 1;
    }
  }

  &--done {
    background-color: rgba(34, 41, 47, 0.02);

    .task-title {
      text-decoration: line-through;
      color: #b9b9c3;
    }
    .task-description {
      color: #b9b9c3;
    }
  }

  &--overdue {
    border-left-color: #ea5455;
  }

  &--ghost {
    opacity: 0.5;
    border-style: dashed;
    background-color: rgba(115, 103, 240, 0.06);
  }
}
.task-item__check {
  margin-right: 0.5rem;
}
.task-item__delete {
  opacity: 0;
  transition: opacity 0.2s ease;
}
.draggable-task-handle {
  cursor: grab;
  color: #b9b9c3;
  margin-right: 0.5rem;
  margin-top: 3px;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
}
.task-content {
  flex: 1;
  min-width: 0;
}
.task-main {
  min-width: 0;
}
.task-title {
  font-weight: 500;
}
.task-description {
  font-size: 0.85rem;
  color: #6e6b7b;
  margin-top: 2px;
}
.task-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
}
.task-meta__badge {
  display: inline-flex;
  align-items: center;
  font-weight: 500;
}
.task-placeholder {
  padding: 0.9rem 1rem;
  border: 1px dashed rgba(34, 41, 47, 0.15);
  border-radius: 8px;
  color: #b9b9c3;
  font-size: 0.85rem;
  text-align: center;
}

.dark-layout .task-item {
  background-color: #283046;
  border-color: rgba(255, 255, 255, 0.08);
}
</style>
